import { FC, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';

import type { ActiveTemplateCategoryMeta } from '../../tenant-profile-hub/api/types';
import { CategoryTabs } from '../../tenant-profile-hub/organization-profile/components/CategoryTabs';
import { FieldRenderer } from '../../tenant-profile-hub/organization-profile/components/fields/FieldRenderer';
import { SectionLabel } from '../../tenant-profile-hub/organization-profile/components/SectionLabel';
import { categoryIcon } from '../../tenant-profile-hub/organization-profile/utils/categoryIcon';
import {
  deriveCategoryStatus,
  type DerivedCategory,
} from '../../tenant-profile-hub/organization-profile/utils/deriveCategoryStatus';
import { ORG_TOKENS } from '../../tenant-profile-hub/organization-profile/utils/tokens';
import { useAdminTenantProfileCategories, useAdminTenantProfileCategory } from '../api/admin-api';

import { adminColors } from './adminTheme';

const isWide = (type: string) => type === 'table' || type === 'textarea';

/** Read-only panel for the active category: header + two-column field grid. */
const ReadOnlyCategoryPanel: FC<{
  tenantId: string;
  meta: ActiveTemplateCategoryMeta;
  derived: DerivedCategory;
}> = observer(({ tenantId, meta, derived }) => {
  const { t } = useTrans();
  const categoryQuery = useAdminTenantProfileCategory(tenantId, meta.id);
  const category = categoryQuery.data;

  const name = meta.metadata.name || meta.key;
  const description = meta.metadata.description;
  const IconCmp = categoryIcon(meta.key, name);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.25,
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
              backgroundColor: 'grey.200',
              border: 1,
              borderColor: 'grey.300',
            }}
          >
            <IconCmp size={18} />
          </Box>
          <Typography variant="textLG_bold" color="text.primary">
            {name}
          </Typography>
          <Box
            component="span"
            sx={{
              typography: 'textXS_semiBold',
              px: 1,
              py: '3px',
              borderRadius: 999,
              backgroundColor: 'grey.200',
              color: 'text.tertiary',
            }}
          >
            <Box component="span" sx={{ typography: 'textXS_bold', color: 'green.700' }}>
              {derived.filledCount}
            </Box>{' '}
            {t('app.orgProfile.panel.fieldsFilledSuffix', { total: derived.totalFields })}
          </Box>
        </Box>
        {description && (
          <Typography variant="textSM_regular" color="text.tertiary">
            {description}
          </Typography>
        )}
      </Box>

      <Box sx={{ height: '1px', backgroundColor: 'grey.300' }} />

      {categoryQuery.isError ? (
        <Typography variant="textSM_regular" color="yellow.800">
          {t('app.orgProfile.loadError')}
        </Typography>
      ) : !category ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <SectionLabel>{t('app.orgProfile.panel.approvedValues')}</SectionLabel>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              columnGap: 2,
              rowGap: 2,
            }}
          >
            {category.fields.map((field) => (
              <Box
                key={field.id}
                sx={{ gridColumn: isWide(field.type) ? '1 / -1' : 'auto', minWidth: 0, overflow: 'hidden' }}
              >
                <FieldRenderer field={field} mode="read" value={field.currentValue} />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
});

/**
 * Read-only admin view of a tenant's compliance profile, mirroring the
 * normal-user Organization Profile: a category chip selector plus the selected
 * category's fields shown as approved values. No editing affordances.
 */
export const ComplianceProfileTab: FC<{ tenantId: string }> = observer(({ tenantId }) => {
  const theme = useTheme();
  const c = adminColors(theme);
  const { t } = useTrans();
  const { data: outline, isLoading } = useAdminTenantProfileCategories(tenantId);

  const categories = useMemo(() => outline?.categories ?? [], [outline]);
  const derivedById = useMemo(() => {
    const map = new Map<string, DerivedCategory>();
    for (const meta of categories) map.set(meta.id, deriveCategoryStatus(meta));
    return map;
  }, [categories]);

  const [activeId, setActiveId] = useState<string | null>(null);

  // Default to the first category; recover if the active one disappears.
  useEffect(() => {
    if (!categories.length) {
      setActiveId(null);
      return;
    }
    setActiveId((prev) =>
      prev && categories.some((cat) => cat.id === prev) ? prev : categories[0].id,
    );
  }, [categories]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!categories.length) {
    return (
      <Typography sx={{ fontSize: 13, color: c.faint, py: 4, textAlign: 'center' }}>
        {t('app.admin.detail.compliance.empty')}
      </Typography>
    );
  }

  const activeMeta = categories.find((cat) => cat.id === activeId);
  const activeDerived = activeMeta ? derivedById.get(activeMeta.id) : undefined;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <CategoryTabs
        categories={categories}
        derivedById={derivedById}
        activeId={activeId}
        onSelect={setActiveId}
      />
      {activeMeta && activeDerived && (
        <Box
          sx={{
            p: 2.5,
            backgroundColor: 'background.paper',
            border: 1,
            borderColor: 'grey.300',
            borderRadius: `${ORG_TOKENS.radiusMd}px`,
          }}
        >
          <ReadOnlyCategoryPanel
            key={activeMeta.id}
            tenantId={tenantId}
            meta={activeMeta}
            derived={activeDerived}
          />
        </Box>
      )}
    </Box>
  );
});
